import React from 'react'
import { Locale, Translation } from 'locale'
import { HaircutTypeHelper } from 'model'
import { PendingScrollContainer, PendingTable, PendingValidationCell, PendingContainer, ValidationStatus } from 'view/shared/upload'
import { CurvePriceUpload } from './parseCurveSpreadsheet'
import { CUSTOM_OE_TERM_PATTERN, CUSTOM_TERM_PATTERN, PendingCurveFields } from './validateCurveParseResult'
import { PendingIncomingPanelProps } from 'view/shared/incomingUpload/PendingIncomingPanel'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { useSelector } from 'react-redux'

export interface PendingCurvePriceUploadPanelProps extends PendingIncomingPanelProps {
  priceUploads: CurvePriceUpload[]
}

const PendingPanel: React.FC<PendingCurvePriceUploadPanelProps> = ({ priceUploads }) => {
  const userPreferences = useSelector(createUserPreferenceSelector())
  const hasCustomTerm = priceUploads.some(u => !!u.term?.match(CUSTOM_TERM_PATTERN))
  const hasNoOpenEndedTerm = priceUploads.every(u => !u.term?.match(CUSTOM_OE_TERM_PATTERN))

  return (
    <PendingScrollContainer>
      <PendingTable>
        <thead>
          <tr>
            <th>{Locale.translate(Translation.TC_COLLECTION_ID)}</th>
            <th>{Locale.translate(Translation.TC_TERM)}</th>
            {hasCustomTerm && <th>{Locale.translate(Translation.TC_START_DATE)}</th>}
            {hasCustomTerm && hasNoOpenEndedTerm && <th>{Locale.translate(Translation.TC_END_DATE)}</th>}
            <th>{Locale.translate(Translation.TC_RATE)}</th>
            <th>{Locale.translate(Translation.TC_HAIRCUT)}</th>
            <th>{Locale.translate(Translation.TC_HAIRCUT_TYPE)}</th>
          </tr>
        </thead>
        <tbody>
          {priceUploads.map((priceUpload, idx) => {
            const { collectionId, term, startDate, endDate, rate, haircut, haircutType, dataIssues } = priceUpload

            return (
              <tr key={idx}>
                <PendingValidationCell
                  fieldName={PendingCurveFields.COLLECTION_ID}
                  rowValidationStatus={dataIssues.length > 0 ? ValidationStatus.ERROR : ValidationStatus.DEFAULT}
                  validationIssues={dataIssues}>
                  {collectionId}
                </PendingValidationCell>
                <PendingValidationCell fieldName={PendingCurveFields.TERM} validationIssues={dataIssues}>
                  {term}
                </PendingValidationCell>
                {hasCustomTerm && (
                  <PendingValidationCell fieldName={PendingCurveFields.START_DATE} validationIssues={dataIssues}>
                    {Locale.formatDate(startDate, userPreferences.dateFormat)}
                  </PendingValidationCell>
                )}
                {hasCustomTerm && hasNoOpenEndedTerm && (
                  <PendingValidationCell fieldName={PendingCurveFields.END_DATE} validationIssues={dataIssues}>
                    {Locale.formatDate(endDate, userPreferences.dateFormat)}
                  </PendingValidationCell>
                )}
                <PendingValidationCell fieldName={PendingCurveFields.RATE} validationIssues={dataIssues}>
                  {Locale.formatNumber(rate, { percent: true })}
                </PendingValidationCell>
                <PendingValidationCell fieldName={PendingCurveFields.HAIRCUT} validationIssues={dataIssues}>
                  {Locale.formatNumber(haircut, { percent: true })}
                </PendingValidationCell>
                <PendingValidationCell fieldName={PendingCurveFields.HAIRCUT_TYPE} validationIssues={dataIssues}>
                  {haircutType && HaircutTypeHelper.getName(haircutType)}
                </PendingValidationCell>
              </tr>
            )
          })}
        </tbody>
      </PendingTable>
    </PendingScrollContainer>
  )
}

export const PendingCurvePriceUploadPanel: React.FC<PendingCurvePriceUploadPanelProps> = ({ priceUploads }) => {
  return (
    <PendingContainer>
      <h4>{Locale.translate(Translation.TC_PENDING_PRICE_UPLOADS)}</h4>
      <PendingPanel priceUploads={priceUploads} />
    </PendingContainer>
  )
}
